interface ZippopotamPlace {
  'place name': string;
  longitude: string;
  state: string;
  'state abbreviation': string;
  latitude: string;
}

interface ZippopotamResponse {
  'post code': string;
  country: string;
  'country abbreviation': string;
  places: ZippopotamPlace[];
}

/**
 * Service for interacting with Zippopotam API
 * @see https://api.zippopotam.us/
 */
export const zippopotamService = {
  /**
   * Fetches location information by postal code
   * @param countryCode - ISO 3166-1 alpha-2 country code (e.g., 'US' for United States)
   * @param postalCode - Postal code to lookup
   * @param signal - Optional AbortSignal to cancel the request
   * @returns Promise with location information
   * @throws Error if the request fails or returns invalid data
   */
  getLocationByPostalCode: async (
    countryCode: string,
    postalCode: string,
    signal?: AbortSignal
  ): Promise<ZippopotamResponse> => {
    try {
      const response = await fetch(
        `https://api.zippopotam.us/${countryCode}/${postalCode}`,
        { signal }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data as ZippopotamResponse;
    } catch (error) {
      // Перекидаємо AbortError далі без додаткової обробки
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      }
      
      throw new Error(
        `Failed to fetch location data: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  },
};

/**
 * React Query hook for Zippopotam API
 */
export const useZippopotamQuery = () => ({
  /**
   * Query for fetching location information by postal code
   * @param countryCode - ISO 3166-1 alpha-2 country code (e.g., 'US' for United States)
   * @param postalCode - Postal code to lookup
   * @returns React Query configuration
   */
  locationByPostalCodeQuery: (countryCode: string, postalCode: string) => ({
    queryKey: ['zippopotam', countryCode, postalCode],
    queryFn: (context?: { signal?: AbortSignal }) => 
      zippopotamService.getLocationByPostalCode(countryCode, postalCode, context?.signal),
    enabled: !!countryCode && !!postalCode,
    retry: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes
  }),
});
